<script setup>
import LocationInput from "./components/LocationInput.vue";
import LocationMap from "./components/LocationMap.vue";
import LocationList from "./components/LocationList.vue";
import LocationDetail from "./components/LocationDetail.vue";
import { useLocationsStore } from "./stores/locationsStore";

const locationsStore = useLocationsStore();
</script>

<template>
  <transition name="fade" v-if="locationsStore.detailExpanded">
    <Suspense timeout="0">
      <LocationDetail class="fixed w-full md:w-3/5 lg:w-1/3 z-40" />
    </Suspense>
  </transition>
  <div class="flex flex-col md:flex md:flex-row ps-h-screen">
    <div class="flex flex-row justify-center md:hidden px-4 py-2">
      <a href="https://pinseekergame.com">
        <img
          src="@/venue_locator/assets/images/pinseeker-logo-horizontal.svg"
          class="h-6"
        />
      </a>
      <div class="flex items-center">
        <p class="text-white pl-4">Where to Play</p>
      </div>
    </div>
    <LocationMap />
    <div
      id="content"
      class="grow flex flex-col order-2 md:order-1 md:flex md:flex-col w-full md:w-3/5 lg:w-1/3 bg-black md:h-full md:border-r md:border-gray-800 overflow-scroll"
    >
      <LocationInput />
      <div
        class="relative flex grow sidebar-content-container md:h-full md:overflow-scroll"
      >
        <LocationList />
      </div>
      <footer
        class="hidden sm:block bg-black w-full z-20 relative border-t border-gray-800"
      >
        <div
          class="py-2 sm:py-4 px-4 sm:px-6 flex items-center justify-between"
        >
          <div class="justify-center md:order-1 md:mt-0">
            <p class="text-center text-xs leading-5 text-gray-500">
              &copy; {{ new Date().getFullYear() }} PinSeeker, Inc.
            </p>
          </div>
          <div class="flex justify-center space-x-2 md:order-2">
            <a
              href="https://www.facebook.com/pinseekergame"
              target="_blank"
              class="text-gray-400 hover:text-gray-500"
            >
              <span class="sr-only">Facebook</span>
              <svg
                class="h-5 w-5"
                fill="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  fill-rule="evenodd"
                  d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                  clip-rule="evenodd"
                />
              </svg>
            </a>
            <a
              href="https://www.instagram.com/pinseekergame/"
              target="_blank"
              class="text-gray-400 hover:text-gray-500"
            >
              <span class="sr-only">Instagram</span>
              <svg
                class="h-5 w-5"
                fill="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  fill-rule="evenodd"
                  d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
                  clip-rule="evenodd"
                />
              </svg>
            </a>
            <a
              href="https://www.youtube.com/@PinSeekerGame"
              target="_blank"
              class="pl-0.5 text-gray-400 hover:text-gray-500"
            >
              <span class="sr-only">YouTube</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"
                />
              </svg>
            </a>
          </div>
        </div>
      </footer>
    </div>
  </div>
</template>
